import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, User, LogOut, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNexusAuth } from "@/context/NexusAuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import "@/pages/nexus/nexus.css";

export default function NexusNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const { t } = useTranslation();
  const { buyer, logout } = useNexusAuth() || {};

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/nexus/listings", label: t("nexus.nav.marketplace") },
    { to: "/nexus/sell", label: t("nexus.nav.forSellers") },
    { to: "/nexus/buy-side-request", label: t("nexus.nav.buySide") },
  ];

  return (
    <>
      {/* Slim top strip: back to main Corporaco site */}
      <div
        style={{
          background: "rgba(10,11,13,0.98)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nexus-back-to-main"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)]/70 hover:text-[var(--bone)] transition-colors"
          >
            <ArrowLeft size={12} /> {t("nexus.nav.backToMain")}
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--bone-2)]/50 hidden sm:block">
            {t("nexus.nav.aMarketplace")}
          </div>
        </div>
      </div>

      <header
        data-testid="nexus-nav"
        className="sticky top-0 z-40 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(10,11,13,0.94)" : "rgba(10,11,13,0.78)",
          borderBottom: "1px solid var(--line)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-4">
          <Link to="/nexus" data-testid="nexus-logo" className="nx-brand" aria-label="Corpora Nexus — home">
            <img
              src="/brand/corpora-wordmark.png?v=4"
              alt="Corpora"
              className="nx-brand-corpora"
            />
            <span className="nx-brand-sep">·</span>
            <span className="nx-brand-nexus">Nexus</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l, i) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nexus-nav-${i}`}
                className={({ isActive }) =>
                  `px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    isActive ? "text-[var(--pewter)]" : "text-[var(--bone-2)] hover:text-[var(--bone)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <span className="mx-3 w-px h-4 bg-[var(--line-2)]" />
            {buyer ? (
              <>
                <Link
                  to="/nexus/deals"
                  data-testid="nexus-nav-deals"
                  className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                >
                  Deals
                </Link>
                <Link
                  to="/nexus/watchlist"
                  data-testid="nexus-nav-watchlist"
                  className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                >
                  Watchlist
                </Link>
                <Link
                  to="/nexus/account"
                  data-testid="nexus-nav-account"
                  className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                >
                  <User size={13} /> {buyer.full_name?.split(" ")[0] || t("nexus.nav.account")}
                </Link>
                <button
                  onClick={logout}
                  data-testid="nexus-nav-logout"
                  title={t("nexus.nav.signOut")}
                  className="p-2 text-[var(--bone-2)]/60 hover:text-[var(--bone)]"
                >
                  <LogOut size={14} />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/nexus/signin"
                  data-testid="nexus-nav-signin"
                  className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--bone-2)] hover:text-[var(--bone)]"
                >
                  {t("nexus.nav.signIn")}
                </Link>
                <Link
                  to="/nexus/register"
                  data-testid="nexus-nav-register"
                  className="ml-3 nx-btn nx-btn-primary !py-2.5 !px-5"
                >
                  {t("nexus.nav.qualify")}
                </Link>
              </>
            )}
            <span className="mx-2 w-px h-4 bg-[var(--line-2)]" />
            <LanguageSwitcher variant="nav" />
          </nav>

          <button
            className="md:hidden p-2 text-[var(--bone)]"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("nexus.nav.menu")}
            data-testid="nexus-nav-burger"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden" style={{ background: "rgba(10,11,13,0.98)", borderTop: "1px solid var(--line)" }}>
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="font-mono text-[12px] uppercase tracking-[0.22em] text-[var(--bone-2)]" data-testid={`nexus-nav-m-${l.to}`}>
                  {l.label}
                </Link>
              ))}
              <div className="h-px bg-[var(--line)] my-2" />
              {buyer ? (
                <>
                  <Link to="/nexus/deals" className="font-mono text-[12px] uppercase tracking-[0.22em] text-[var(--bone-2)]">
                    Deals
                  </Link>
                  <Link to="/nexus/watchlist" className="font-mono text-[12px] uppercase tracking-[0.22em] text-[var(--bone-2)]">
                    Watchlist
                  </Link>
                  <Link to="/nexus/account" className="font-mono text-[12px] uppercase tracking-[0.22em] text-[var(--pewter)]">
                    {t("nexus.nav.account")}
                  </Link>
                  <button onClick={logout} className="text-left font-mono text-[12px] uppercase tracking-[0.22em] text-[var(--bone-2)]/70">
                    {t("nexus.nav.signOut")}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/nexus/signin" className="font-mono text-[12px] uppercase tracking-[0.22em] text-[var(--bone-2)]">{t("nexus.nav.signIn")}</Link>
                  <Link to="/nexus/register" className="nx-btn nx-btn-primary self-start">{t("nexus.nav.qualifyAsBuyer")}</Link>
                </>
              )}
              <div className="h-px bg-[var(--line)] my-2" />
              <LanguageSwitcher variant="nav" />
              <Link
                to="/"
                data-testid="nexus-nav-m-back"
                className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)]/70"
              >
                <ArrowLeft size={11} /> {t("nexus.nav.backToMain")}
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
